import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import { InputNumber } from "antd";
import Link from "next/link";
import { ICategory } from "@/props/IProduct";
import Icon from "../Icon";

/**
 * 商品标题
 * @param title
 */
const ProductTitle = ({ title }: { title: string }) => {
  return <div className="text-base font-semibold leading-6 lg:text-[30px] lg:font-medium lg:leading-9">{title}</div>;
};

/**
 * 商品价格
 * @param price
 */
const ProductPrice = ({ price }: { price: number | undefined }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-2 flex items-center lg:mt-6">
      <div className="mr-2 text-base font-semibold text-primary lg:text-2xl">{price}</div>
      <div className="text-[10px] text-stone-100 sm:text-xs lg:text-sm">{t("common.dream_value")}</div>
    </div>
  );
};

/**
 * 商品描述
 * @param desc
 */
const ProductDesc = ({ desc }: { desc: string }) => {
  return <div className="mt-4 border-b border-gray-100 pb-4 text-stone-100 lg:pb-8">{desc}</div>;
};

// 用顿号做分隔的语言。其余语言用半角逗号
const IDEOGRAPHIC_COMMA_LOCALES = ["sc", "tc", "jp"];

/**
 * 商品区域
 *
 * 可邮寄区域常见是 200+ 个国家，全量平铺会把下面的加购/立即兑换按钮顶到首屏外，
 * 所以默认只显示两行，展开后限高滚动，页面整体高度不变。
 * @param country 国家码数组
 */
const ProductCountry = ({ country }: { country: any[] }) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [collapsible, setCollapsible] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const names = country.map((item) => t(`country.${item}`));
  // 原来靠 flex + mr-2 排列，国家名之间只有空隙，"Congo (DRC) Central Africa Congo"
  // 这种连着读根本分不出边界，改成带分隔符的整段文本
  const separator = IDEOGRAPHIC_COMMA_LOCALES.includes(i18n.language) ? "、" : ", ";
  const text = names.join(separator);

  // 按国家个数猜有没有超两行不准：15 个短国名（Peru、Chile…）刚好排满两行，
  // 一个字都没被截掉，按钮却照样出现，点了没反应。只能量真实高度。
  // 宽度变了行数也会变，所以跟着容器 resize 重量一次。
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      // 展开态没有 line-clamp，量不出截断，此时不更新（保持展开前的判断）
      if (expanded) return;
      setCollapsible(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div className="mt-4 flex border-b border-gray-100 pb-4 lg:mt-8 lg:pb-8">
      <div className="mr-4 shrink-0 font-medium">{t("product_detail.shipping_area")}: </div>
      <div className="min-w-0 flex-1">
        <div
          ref={textRef}
          className={`text-stone-300 ${expanded ? "max-h-[180px] overflow-y-auto pr-2" : "line-clamp-2"}`}
        >
          {text}
        </div>
        {collapsible && (
          <div
            className="mt-1 flex w-fit cursor-pointer items-center text-primary"
            onClick={() => setExpanded(!expanded)}
            data-qt="mall_detail_shipping_area_toggle"
            data-qt-expanded={expanded ? 0 : 1}
            data-qt-country-count={names.length}
          >
            {expanded ? t("product_category.expand") : `${t("product_category.more")} (${names.length})`}
            <Icon name="arrow-down" size={20} className={`ml-1 ${expanded ? "rotate-180" : ""}`} />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 商品品牌
 * @param brand
 */
const ProductBrand = ({ brand }: { brand: string }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 flex items-center lg:mt-8">
      <div className="mr-4">{t("product_detail.product_brand")}:</div>
      <div className="text-stone-300">{brand}</div>
    </div>
  );
};

/**
 * 商品库存
 * @param stock
 */
const ProductStock = ({ stock }: { stock: number }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 flex items-center">
      <div className="mr-4">{t("product_detail.product_stock")}:</div>
      <div className="text-stone-300">{stock ? t("product_detail.in_stock") : 0}</div>
    </div>
  );
};

/**
 * 商品数量
 */
const ProductNum = ({
  value = 1,
  max,
  updateCartNum,
  isMobile = false,
}: {
  value: number;
  max: number;
  updateCartNum: (num: number) => void;
  isMobile?: boolean;
}) => {
  const { t } = useTranslation();

  // product quantity minus event
  const handleMinus = () => {
    if (value <= 1) return;
    updateCartNum(value - 1);
  };

  // product quantity plus event
  const handlePlus = () => {
    if (value >= max) return;
    updateCartNum(value + 1);
  };

  // minus button
  const MinusImage = (
    <div
      className={`hover:bg-gray-50 flex h-10 w-10 cursor-pointer items-center justify-center rounded-l-lg border border-r-0 border-stone-400 bg-[#f7f7f7] transition-colors ${
        value <= 1 ? "cursor-not-allowed opacity-40" : ""
      }`}
      onClick={handleMinus}
    >
      <Icon name="reduce" size={16}/>
    </div>
  );

  // plus button
  const PlusImage = (
    <div
      className={`hover:bg-gray-50 flex h-10 w-10 cursor-pointer items-center justify-center rounded-r-lg border border-l-0 border-stone-400 bg-[#f7f7f7] transition-colors ${
        value >= max ? "cursor-not-allowed opacity-40" : ""
      }`}
      onClick={handlePlus}
    >
      <Icon name="plus" size={16} />
    </div>
  );

  // 数量改变
  const onQuantityChange = (value: number | null) => {
    const cartNum = value === null ? 1 : value;
    updateCartNum(cartNum);
  };

  return (
    <div className={`items-center ${!isMobile ? "mt-4 hidden lg:flex" : "mt-6 flex justify-between"}`}>
      <div className="mr-4">{t("product_detail.product_quantity")}:</div>
      <div className="flex items-center">
        {MinusImage}
        <InputNumber
          min={1}
          max={max}
          precision={0}
          size="large"
          value={value}
          style={{
            width: !isMobile ? "64px" : "52px",
            textAlign: "center",
            borderRadius: 0,
          }}
          className="product-num-input text-center"
          controls={false}
          onChange={onQuantityChange}
        />
        {PlusImage}
      </div>
    </div>
  );
};

/**
 * 商品分类
 */
const ProductCategory = ({ categories, currentLocale }: { categories: ICategory[]; currentLocale: string }) => {
  const { t } = useTranslation();

  const getLinearArr = () => {
    let data = JSON.parse(JSON.stringify(categories));
    let newData: any = [];
    const callback = (item: ICategory, i) => {
      (item.child || (item.child = [])).forEach((v) => {
        callback(v, i);
      });
      delete item.child;
      newData[i].unshift(item);
    };
    data.map((v, i) => {
      newData[i] = [];
      callback(v, i);
    });
    return newData;
  };

  let newCategories: any = getLinearArr();

  return (
    <div className="mt-4 flex flex-wrap border-y border-gray-100 py-4 lg:mt-8 lg:border-y-0 lg:py-0">
      <div className="mr-4 shrink-0">{t("product_detail.product_category")}:</div>
      <div className="flex flex-1 flex-wrap items-center">
        {newCategories.map((categoryItem: ICategory[], categoryIndex) => (
          <div className="mr-6 flex flex-wrap items-center" key={categoryIndex}>
            {categoryItem.map((item, index) => (
              <div className="flex flex-wrap items-center" key={item.id}>
                <Link className="text-stone-800 hover:text-primary lg:text-stone-100 transition-colors duration-200" href={`/mall?cid=${item.id}`}>
                  {item.name[currentLocale]}
                </Link>
                {index < categoryItem.length - 1 && <div className="mx-4 h-[14px] w-[1px] bg-stone-100"></div>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <Icon name="arrow-right" isMobile size={16} className="block lg:hidden" />
    </div>
  );
};

export {
  ProductTitle,
  ProductPrice,
  ProductDesc,
  ProductCountry,
  ProductBrand,
  ProductStock,
  ProductNum,
  ProductCategory,
};
